import { pendingAsNow } from "@/utils/timestamps"
import type { Customer, Removable, RemovalKind, Session } from "@/types"

/*
 * Whether a record is still in the working lists, and why it is not.
 *
 * The same job `sessionLifecycle.sessionState` does for a session's own life:
 * one place that resolves the fields into the vocabulary every list, row, badge
 * and dialog renders, so no screen re-derives it and gets it half right.
 *
 * Read off `removedAs`, never off `removedAt`. The stamp is a
 * `serverTimestamp()` and reads back null until the server acknowledges it, so a
 * screen testing "has a removal time" would show the row back in the main list
 * for a whole round trip after the facilitator archived it — on the one write
 * whose entire point is that the row goes away.
 */

export type RemovalState = "active" | RemovalKind

export function removalState(record: Removable | null | undefined): RemovalState {
  return record?.removedAs ?? "active"
}

export function isRemoved(record: Removable | null | undefined): boolean {
  return removalState(record) !== "active"
}

/**
 * Where a session stands, its OWN removal or its customer's.
 *
 * Archiving a customer does not archive their sessions — the product asks for
 * the old work to survive the customer, and a fan-out would also destroy the
 * only thing restore needs to know: whether a session was filed away on its own
 * or merely came along. Nothing is written to the sessions at all. A session
 * whose customer is gone from the lists is gone from them too, and this is where
 * that is decided.
 *
 * A session's own removal wins when it has one. It is the more specific fact,
 * and it is the one that survives restoring the customer: a session deleted as a
 * mistake must not come back just because its customer did.
 *
 * `customer` is null when the caller could not resolve one — a session whose
 * customer document this facilitator cannot read. Treated as present: hiding
 * somebody's session because a second read did not land is the worse failure.
 */
export interface SessionRemoval {
  state: RemovalState
  /**
   * Removed because the customer was, not on its own. The Archive shows these
   * without a Restore button: restoring the customer is what brings them back,
   * and a per-session restore would be a button that does nothing.
   */
  inherited: boolean
}

export function sessionRemoval(
  session: Session,
  customer: Customer | null | undefined
): SessionRemoval {
  const own = removalState(session)
  if (own !== "active") return { state: own, inherited: false }
  return { state: removalState(customer), inherited: isRemoved(customer) }
}

/** Newest removal first, the order the Archive reads in. */
export function byRemovedAt(a: Removable, b: Removable): number {
  return pendingAsNow(b.removedAt) - pendingAsNow(a.removedAt)
}
